import React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { colors } from "../../theme/colors";
import {
  isSupported,
  getPermissionState,
  subscribeToPush,
  registerServiceWorker,
} from "../../utils/webPush";

/**
 * Delicate, low-profile opt-in for browser push notifications.
 *
 * Only shown to Steam-only sign-ins with no email on file — for them, this
 * is the one channel we have to reach out (e.g. "your profile is verified")
 * without asking for an email address.
 */
export default function PushNotificationOptIn({ user, compact = false, buttonOnly = false }) {
  const isEmaillessSteamUser =
    user?.signupSource === "steam" && user?.emailIsSynthetic === true;

  const [permission, setPermission] = React.useState(() => getPermissionState());
  const [subscribed, setSubscribed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!isEmaillessSteamUser || !isSupported() || getPermissionState() !== "granted") {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const registration = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
        const existing = await registration.pushManager.getSubscription();
        if (!cancelled) setSubscribed(Boolean(existing));
      } catch {
        // Non-fatal: worst case the button re-offers to subscribe.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEmaillessSteamUser]);

  if (!isEmaillessSteamUser || !isSupported() || permission === "denied") {
    return null;
  }

  async function handleEnable() {
    setLoading(true);
    setError(null);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPermission(getPermissionState());
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setPermission(getPermissionState());
    } finally {
      setLoading(false);
    }
  }

  if (buttonOnly) {
    return subscribed ? (
      <Chip
        size="small"
        color="success"
        label="Enabled"
        sx={{ height: "31px" }}
      />
    ) : (
      <Button
        variant="outlined"
        size="small"
        onClick={handleEnable}
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress size={14} />
          ) : (
            <NotificationsActiveOutlinedIcon sx={{ fontSize: "1rem" }} />
          )
        }
        sx={{
          minHeight: "31px",
          px: 1.75,
          borderRadius: "14px",
          border: `1px solid rgba(255,255,255,0.06)`,
          background: `
            linear-gradient(
              180deg,
              rgba(255,255,255,0.04),
              rgba(255,255,255,0.015)
            )
          `,
          color: colors.text.primary,
          fontSize: "0.88rem",
          fontWeight: 600,
          textTransform: "none",
          whiteSpace: "nowrap",
          boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.05),
            0 10px 24px rgba(0,0,0,0.24)
          `,
          transition: "all 240ms cubic-bezier(0.22,1,0.36,1)",
          "&:hover": {
            borderColor: alpha(colors.primary.main, 0.18),
            background: `
              linear-gradient(
                180deg,
                rgba(255,255,255,0.05),
                rgba(255,255,255,0.02)
              )
            `,
            color: colors.primary.main,
            boxShadow: `
              inset 0 1px 0 rgba(255,255,255,0.05),
              0 0 24px rgba(246,185,76,0.08),
              0 12px 28px rgba(0,0,0,0.3)
            `,
          },
        }}
      >
        Enable alerts
      </Button>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 2,
        p: 2,
        mb: compact ? 0 : 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <NotificationsActiveOutlinedIcon
          fontSize="small"
          sx={{ color: "rgba(255,255,255,0.5)" }}
        />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            No email? Get notified in your browser.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subscribed
              ? "Enabled — we'll alert you here the moment your profile is verified."
              : "We'll send a one-time browser alert the moment your profile is verified."}
          </Typography>
          {error ? (
            <Typography variant="caption" color="error" display="block">
              {error}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {subscribed ? (
        <Chip size="small" color="success" label="Enabled" />
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={handleEnable}
          disabled={loading}
        >
          {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Enable alerts
        </Button>
      )}
    </Box>
  );
}

PushNotificationOptIn.propTypes = {
  user: PropTypes.shape({
    signupSource: PropTypes.string,
    emailIsSynthetic: PropTypes.bool,
  }),
  compact: PropTypes.bool,
  buttonOnly: PropTypes.bool,
};
